/* eslint-disable react-hooks/exhaustive-deps */
import React, {useEffect, useMemo, useState} from 'react'
import {
    RowSelectionState,
    SortingState,
    flexRender,
    getCoreRowModel,
    getFilteredRowModel,
    getSortedRowModel,
    useReactTable,
} from '@tanstack/react-table'
import {Link, useNavigate} from 'react-router-dom'
import moment from 'moment/moment'
import {ToastContainer} from 'react-toastify'
import {KTCard, KTCardBody, KTIcon} from '../../../_websoft/helpers'
import {
    deleteThermoBoardFmkNalog,
    fetchThermoBoardFmkNalog,
    getAllThermoBoardFmkNalozi,
    updateThermoBoardFmkNalog,
} from '../../api/thermoboardNalozi'
import ExportButton from '../../components/TableExportToExcel/ExportButton'
import LoadingComponent from '../../components/LoadingComponent/LoadingComponent'
import TablePagination from '../../components/TablePagination/TablePagination'
import TableSearch from '../../components/TableSearch/TableSearch'
import notifyToast from '../../components/Toast/Toast'
import {PaginationModel} from '../../models/PaginationModel'
import {TableActionButton} from '../../components/TableActionBtn/TableActionButton'
import IndeterminateCheckbox from '../../components/IndeterminateCheckbox/IndeterminateCheckbox'
import KomercijalistePanelaDropdown
    from '../paneli/components/KomercijalistePanelaDropdown/KomercijalistePanelaDropdown'
import PanelTipDropdown from '../paneli/components/PanelTipDropdown/PanelTipDropdown'
import FmkPartneriDropdown from '../../components/FmkNaloziComponent/FmkParneriDropdown'

type ThermoBoardFmkNalog = {
    id?: number
    Id?: number
    fmk_nalog?: string
    stavka?: number | string | null
    tip_panela?: number | string | { Naziv?: string; naziv?: string }
    komercijalista?: number | string
    rok_isporuke?: string
    kolicina?: number | string
    isporucena_kolicina?: number | string | null
    planirana_proizvodnja?: string
    planirana_kolicina_za_nalog?: number | string | null
    kreirano?: string
}

type FmkListParams = {
    page: number
    per_page: number
    komercijalista?: string | null
    tip_panela?: string | null
    idKupacFMK?: string | null
    status?: string | null
}

const getPaginationTotalPage = (total?: number, perPage?: number) => {
    if (!total || !perPage) {
        return 1
    }

    return Math.max(Math.ceil(total / perPage), 1)
}

const getResponseRows = (responseData: any): ThermoBoardFmkNalog[] => {
    if (Array.isArray(responseData)) {
        return responseData
    }

    if (Array.isArray(responseData?.data)) {
        return responseData.data
    }

    if (Array.isArray(responseData?.data?.data)) {
        return responseData.data.data
    }

    return []
}

const getPaginationSource = (responseData: any) => {
    if (responseData?.current_page || responseData?.per_page) {
        return responseData
    }

    if (responseData?.data?.current_page || responseData?.data?.per_page) {
        return responseData.data
    }

    return null
}

const formatDate = (value?: string) => {
    if (!value) {
        return ''
    }

    return moment(value).isValid() ? moment(value).format('DD-MM-YYYY') : value
}

const getVrstaLabel = (row: ThermoBoardFmkNalog) => {
    if (typeof row.tip_panela === 'object') {
        return row.tip_panela?.Naziv || row.tip_panela?.naziv || ''
    }

    return row.tip_panela || ''
}

const getTipPanelaValue = (data: any) =>
    data?.tip_panela_id?.toString() ??
    data?.TipPanela?.toString() ??
    data?.TipId?.toString() ??
    data?.tip_panela?.Id?.toString() ??
    data?.tip_panela?.id?.toString() ??
    (typeof data?.tip_panela === 'number' || typeof data?.tip_panela === 'string'
        ? data.tip_panela.toString()
        : '')

const getArchivePayload = (data: any) => ({
    fmk_nalog: data?.fmk_nalog ?? '',
    stavka: data?.stavka ? Number(data.stavka) : null,
    idKupacFMK:
        data?.idKupacFMK?.toString() ??
        data?.IdKupacFMK?.toString() ??
        data?.fmk_partner?.toString() ??
        data?.fmk_partner_id?.toString() ??
        data?.partner?.Id?.toString() ??
        data?.partner?.id?.toString() ??
        '',
    nazivKupca:
        data?.nazivKupca ??
        data?.KupacNaziv ??
        data?.kupac?.Naziv ??
        data?.kupac?.naziv ??
        data?.partner?.Naziv ??
        data?.partner?.naziv ??
        '',
    tip_panela: getTipPanelaValue(data),
    komercijalista: data?.komercijalista?.toString() ?? '',
    rok_isporuke: data?.rok_isporuke ? data.rok_isporuke.slice(0, 10) : '',
    kolicina: Number(data?.kolicina),
    isporucena_kolicina: data?.isporucena_kolicina ? Number(data.isporucena_kolicina) : null,
    planirana_proizvodnja: data?.planirana_proizvodnja ? data.planirana_proizvodnja.slice(0, 10) : null,
    planirana_kolicina_za_nalog: data?.planirana_kolicina_za_nalog
        ? Number(data.planirana_kolicina_za_nalog)
        : null,
    status: 0,
})

export type ThermoBoardFmkListaProps = {
    hideDodajButton?: boolean
    hideAkcijaColumn?: boolean
}

function ThermoBoardFmkLista({
    hideDodajButton = false,
    hideAkcijaColumn = false,
}: ThermoBoardFmkListaProps) {
    const [data, setData] = useState<ThermoBoardFmkNalog[]>([])
    const [loading, setLoading] = useState(true)
    const [archiveLoading, setArchiveLoading] = useState(false)
    const [sorting, setSorting] = useState<SortingState>([])
    const [rowSelection, setRowSelection] = useState<RowSelectionState>({})
    const [globalFilter, setGlobalFilter] = useState('')
    const [filters, setFilters] = useState({
        komercijalista: '',
        tip_panela: '',
        idKupacFMK: '',
        status: '1',
    })
    const navigate = useNavigate()

    const [pagination, setPagination] = useState<PaginationModel>({
        curPage: 1,
        totalPage: 1,
        perPage: 10,
    })
    const [params, setParams] = useState<FmkListParams>({
        page: 1,
        per_page: 10,
        status: '1',
    })

    const handleFilterChange = (
        field: keyof typeof filters,
        value: string | number | null | undefined
    ) => {
        const filterValue = value ? value.toString() : ''

        setFilters((prev) => ({
            ...prev,
            [field]: filterValue,
        }))

        setParams((prev) => ({
            ...prev,
            page: 1,
            [field]: filterValue || null,
        }))
    }

    const updatePage = () => {
        setLoading(true)
        getAllThermoBoardFmkNalozi(params)
            .then((response) => {
                const responseData = response.data
                const paginationData = getPaginationSource(responseData)

                setData(getResponseRows(responseData))
                setRowSelection({})
                setPagination({
                    curPage: paginationData?.current_page ?? params.page,
                    totalPage: getPaginationTotalPage(
                        paginationData?.total,
                        paginationData?.per_page ?? params.per_page
                    ),
                    perPage: paginationData?.per_page ?? params.per_page,
                })
            })
            .catch((error) => {
                notifyToast(false, error?.response?.data?.message || 'Greska pri ucitavanju FMK naloga.')
            })
            .finally(() => setLoading(false))
    }

    const onDelete = (id: number) => {
        deleteThermoBoardFmkNalog(id)
            .then((response) => {
                if (response.error === 0) {
                    notifyToast(true, response.message)
                    updatePage()
                } else {
                    notifyToast(false, response.message)
                }
            })
            .catch((error) => {
                notifyToast(false, error.message)
            })
    }
    const onEdit = (rowData) => {
        navigate(`/thermoboard/fmk-nalozi/izmjena/${rowData}`)
    }

    useEffect(() => {
        updatePage()
    }, [params])

    const columns = useMemo(
        () => [
            {
                id: 'select',
                header: ({table}) => (
                    <IndeterminateCheckbox
                        checked={table.getIsAllRowsSelected()}
                        indeterminate={table.getIsSomeRowsSelected()}
                        onChange={table.getToggleAllRowsSelectedHandler()}
                    />
                ),
                cell: ({row}) => (
                    <IndeterminateCheckbox
                        checked={row.getIsSelected()}
                        disabled={!row.getCanSelect()}
                        indeterminate={row.getIsSomeSelected()}
                        onChange={row.getToggleSelectedHandler()}
                    />
                ),
                enableSorting: false,
            },
            {
                id: 'id',
                header: 'Id',
                accessorFn: (row: ThermoBoardFmkNalog) => row.id ?? row.Id,
            },
            {
                id: 'fmk_nalog',
                header: 'FMK nalog',
                accessorKey: 'fmk_nalog',
            },
            {
                id: 'stavka',
                header: 'Stavka',
                accessorKey: 'stavka',
            },
            {
                id: 'Kupac',
                header: 'Kupac',
                accessorKey: 'nazivKupca',
            },
            {
                id: 'tip_panela',
                header: 'Tip panela',
                accessorFn: getVrstaLabel,
            },
            {
                id: 'komercijalista',
                header: 'Komercijalista',
                accessorKey: 'komercijalista',
            },
            {
                id: 'rok_isporuke',
                header: 'Rok isporuke',
                accessorFn: (row: ThermoBoardFmkNalog) => formatDate(row.rok_isporuke),
            },
            {
                accessorKey: 'kolicina',
                header: 'Količina m²',
                cell: ({getValue}) => `${Number(getValue()).toFixed(2)}`,
            },

            {
                id: 'isporucena_kolicina',
                header: 'Isporuceno',
                accessorFn: (row: ThermoBoardFmkNalog) => row.isporucena_kolicina ?? 0,
            },
            {
                id: 'ostalo',
                header: 'Ostalo za proizvest',
                accessorFn: (row: ThermoBoardFmkNalog) =>
                    Number(row.kolicina ?? 0) - Number(row.isporucena_kolicina ?? 0),
                cell: ({getValue}) => `${Number(getValue()).toFixed(2)}`,
            },
            {
                id: 'planirana_proizvodnja',
                header: 'Planirana proizvodnja',
                accessorFn: (row: ThermoBoardFmkNalog) => formatDate(row.planirana_proizvodnja),
                cell: ({row}) => (
                    <div>
                        <div>{formatDate(row.original.planirana_proizvodnja)}</div>
                        {row.original.planirana_kolicina_za_nalog !== null &&
                            row.original.planirana_kolicina_za_nalog !== undefined &&
                            row.original.planirana_kolicina_za_nalog !== '' && (
                                <div>({Number(row.original.planirana_kolicina_za_nalog).toFixed(2)})</div>
                            )}
                    </div>
                ),
            },

            {
                id: 'kreirano',
                header: 'kreirano',
                accessorFn: (row: ThermoBoardFmkNalog) => formatDate(row.kreirano),
            },
            !hideAkcijaColumn && {
                header: 'Akcija',
                accessorKey: 'Akcija',
                cell: ({row}) => (
                    <TableActionButton
                        onDelete={() => onDelete(row.original.id)}
                        onEdit={() => {
                            onEdit(row.original.id)
                        }}
                    />
                ),
            },
        ].filter(Boolean) as any[],
        [hideAkcijaColumn]
    )

    const table = useReactTable({
        data: data,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        state: {
            sorting,
            globalFilter,
            rowSelection,
        },
        onSortingChange: setSorting,
        onGlobalFilterChange: setGlobalFilter,
        onRowSelectionChange: setRowSelection,
        enableRowSelection: true,
        getRowId: (row) => String(row.id ?? row.Id),
    })

    const selectedRowsCount = table.getSelectedRowModel().rows.length

    const handleArchiveSelected = async () => {
        const selectedIds = table
            .getSelectedRowModel()
            .rows.map((row) => row.original.id ?? row.original.Id)
            .map((id) => Number(id))
            .filter((id) => Number.isFinite(id))

        if (!selectedIds.length) {
            notifyToast(false, 'Odaberite naloge za arhiviranje.')
            return
        }

        setArchiveLoading(true)

        try {
            const responses = await Promise.all(
                selectedIds.map(async (id) => {
                    const nalog = await fetchThermoBoardFmkNalog(id)
                    return updateThermoBoardFmkNalog(getArchivePayload(nalog), id)
                })
            )
            const failedResponse = responses.find((response) => response?.error && response.error !== 0)

            if (failedResponse) {
                notifyToast(false, failedResponse.message || 'Greska pri arhiviranju naloga.')
                return
            }

            notifyToast(true, 'Nalozi su arhivirani.')
            setRowSelection({})
            updatePage()
        } catch (error: any) {
            notifyToast(false, error?.response?.data?.message || error?.message || 'Greska pri arhiviranju naloga.')
        } finally {
            setArchiveLoading(false)
        }
    }

    return (
        <KTCard>
            <ToastContainer/>
            <KTCardBody className='py-6'>
                <div className='rounded bg-light p-5 mb-6'>
                    <div className='d-flex flex-column flex-lg-row justify-content-between gap-5'>
                        <div>
                            <h3 className='fs-4 fw-bold text-gray-900 mb-1'>Thermoboard FMK nalozi</h3>
                            <div className='text-muted fw-semibold fs-7'>
                                Pregled FMK naloga i osnovnih komercijalnih podataka.
                            </div>
                        </div>

                        {!hideDodajButton && (
                            <Link to='/thermoboard/fmk-nalozi/dodaj'>
                                <button type='button' className='btn btn-primary fw-bold w-100'>
                                    <KTIcon iconName='plus' className='fs-2'/>
                                    Dodaj
                                </button>
                            </Link>
                        )}
                    </div>
                </div>

                <div className='rounded border bg-light p-4 p-md-5 mb-5'>
                    <div className='d-flex align-items-center gap-3 mb-4'>
                        <div className='symbol symbol-35px'>
                            <div className='symbol-label bg-white'>
                                <KTIcon iconName='filter' className='fs-3 text-primary'/>
                            </div>
                        </div>
                        <div>
                            <div className='fw-bold text-gray-900'>Filteri</div>

                        </div>
                    </div>

                    <div className='row g-4'>
                        <div className='col-12 col-lg-4'>
                            <label className='form-label fw-bold fs-6'>Komercijalista</label>
                            <KomercijalistePanelaDropdown
                                value={filters.komercijalista}
                                onChange={(_, value) => handleFilterChange('komercijalista', value)}
                                selectName='komercijalista'
                            />
                        </div>

                        <div className='col-12 col-lg-4'>
                            <label className='form-label fw-bold fs-6'>Tip panela</label>
                            <PanelTipDropdown
                                value={filters.tip_panela}
                                onChange={(_, value) => handleFilterChange('tip_panela', value)}
                                selectName='tip_panela'
                            />
                        </div>

                        <div className='col-12 col-lg-4'>
                            <label className='form-label fw-bold fs-6'>Status</label>
                            <select
                                name='status'
                                value={filters.status}
                                onChange={(event) => handleFilterChange('status', event.target.value)}
                                className='form-select form-select-solid form-select-lg'
                            >
                                <option value='1'>Aktivni nalozi</option>
                                <option value='0'>Arhivirani nalozi</option>
                            </select>
                        </div>

                    </div>
                </div>

                <div className='d-flex flex-column flex-md-row justify-content-between align-items-md-end gap-4 mb-4'>
                    <div>
                        <label className='form-label fw-bold fs-6'>Traži</label>
                        <TableSearch onSearch={(text) => setGlobalFilter(text)}/>
                    </div>

                    <div data-kt-user-table-toolbar='base' className='d-flex flex-wrap justify-content-md-end gap-3'>
                        <button
                            type='button'
                            className='btn btn-light-danger fw-bold'
                            onClick={handleArchiveSelected}
                            disabled={!selectedRowsCount || archiveLoading}
                        >
                            {archiveLoading ? (
                                <>
                                    <span className='spinner-border spinner-border-sm me-2'/>
                                    Arhiviranje...
                                </>
                            ) : (
                                <>
                                    <KTIcon iconName='archive' className='fs-2'/>
                                    Arhiviraj naloge
                                    {selectedRowsCount > 0 && (
                                        <span className='badge badge-light ms-2'>{selectedRowsCount}</span>
                                    )}
                                </>
                            )}
                        </button>
                        <ExportButton table={table} filename='thermoboard_fmk_nalozi'/>
                    </div>
                </div>

                {loading ? (
                    <LoadingComponent/>
                ) : (
                    <div className='table-responsive mt-3 border rounded'>
                        <table className='table table-row-bordered table-row-gray-200 align-middle gy-4 mb-0 no-footer'>
                            <thead>
                            {table.getHeaderGroups().map((headerGroup) => (
                                <tr
                                    key={headerGroup.id}
                                    className='text-start fw-bolder fs-7 text-uppercase gs-0 bg-dark p-2'
                                >
                                    {headerGroup.headers.map((header) => (
                                        <th
                                            className='text-white ps-4'
                                            key={header.id}
                                            onClick={
                                                header.column.getCanSort()
                                                    ? header.column.getToggleSortingHandler()
                                                    : undefined
                                            }
                                        >
                                            {flexRender(header.column.columnDef.header, header.getContext())}
                                            {/* @ts-ignore */}
                                            {{asc: 'ASC', desc: 'DESC'}[header.column.getIsSorted() ?? null]}
                                        </th>
                                    ))}
                                </tr>
                            ))}
                            </thead>
                            <tbody className='text-gray-600'>
                            {table.getRowModel().rows.length > 0 ? (
                                table.getRowModel().rows.map((row) => (
                                    <tr key={row.id}>
                                        {row.getVisibleCells().map((cell) => (
                                            <td key={cell.id} className='ps-4'>
                                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                            </td>
                                        ))}
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={columns.length}>
                                        <div
                                            className='d-flex text-center w-100 align-content-center justify-content-center py-5'>
                                            Nema pronadjenih FMK naloga.
                                        </div>
                                    </td>
                                </tr>
                            )}
                            </tbody>
                        </table>
                    </div>
                )}

                <TablePagination
                    updatePage={(page, per_page) =>
                        setParams((prev) => ({
                            ...prev,
                            page,
                            per_page,
                        }))
                    }
                    pagination={pagination}
                />
            </KTCardBody>
        </KTCard>
    )
}

export default ThermoBoardFmkLista
